
import WDElement from '../wd-element';
import WDActivity from '../modal/wd-activity';

export default class WDUploader extends WDElement {

	//setup our options
	#options = {		
		fetch: null,										//fetch object we use to upload to
		dnd: null,											//element to monitor for drag and drop file events
		container: document.body,					//container to display file upload message.	defaults to page body
		maxSize: null,									//max size of uploads allowed (bytes)
		multiple: true,									//allows multiple files to be selected for upload
		putFileName: true
	};
						
	#fetch = null;
	#disabled = false;
	#input = null;
	#files = [];
	#uploaded = 0;
	#progressWrapper = null;
	#progressBar = null;
	#progressStatus = null;
	#progressText = null;
	#fileUploaders = new Map(); // Track individual upload instances for event handling

	constructor(opts)
	{
		super().classList.add('wd-uploader-container');

		Object.assign(this.#options,opts);
		this.#fetch = this.#options.fetch;

		// Set up event listeners for the new event-driven WDFetchUpload
		this.setupFetchEventListeners();
		
		if (this.#options.dnd) this.dndUpload();
	}

	/**
	 * Set up event listeners for the new WDFetchUpload event-driven API
	 */
	setupFetchEventListeners()
	{
		// For backwards compatibility with older WDFetchUpload versions
		if (this.#fetch && typeof this.#fetch.setProgressHandler === 'function') {
			this.#fetch.setProgressHandler((evt) => this.handleProgress(evt));
		}
	}

	/**
	 * Create a new uploader instance that properly inherits all configuration from the original
	 */
	createFileUploader()
	{
		// For WDApiUpload, we need to create a new instance with the base URI
		// WDApiUpload constructor handles the full configuration setup including auth
		if (this.#fetch.constructor.name === 'WDApiUpload') {
			const baseURI = this.#fetch.getReqURI();
			if (WDConfig?.debug?.fetch) {
				console.log('Creating WDApiUpload with base URI:', baseURI);
			}
			return new (this.#fetch.constructor)(baseURI);
		}
		
		// For regular WDFetchUpload, clone the config
		const config = { ...this.#fetch.getConfig() };
		if (WDConfig?.debug?.fetch) {
			console.log('Creating WDFetchUpload with config:', config);
		}
		return new (this.#fetch.constructor)(config);
	}

	set fetch(val) { this.#fetch = val; };
	get fetch() { return this.#fetch; };
	get files() { return this.#files; };

	set disabled(disabled) { this.#disabled = disabled; }

	/**
	 * Abort all active uploads
	 */
	abort() {
		this.#fileUploaders.forEach((uploader, file) => {
			if (uploader.isUploading) {
				uploader.abort();
			}
		});
		this.#fileUploaders.clear();
		
		// Clean up UI
		this.empty();
		this.remove();
		if (this.#options.container) {
			this.#options.container.style.position = '';
		}
	}

	/**
		this creates a file input and "clicks" it so the file selector
		prompt is shown
		*/
	upload(formData)
	{
		if (formData) this.#options.formData = formData;
		
		//the actual input form
		this.#input = wdc('<input type="file" style="position:absolute;visibility:hidden;height:0px;float:left;"/>');
		if (this.#options.multiple) this.#input.multiple = true;

		//setup to import images from the camera if the browser supports it
		if (this.#options.camera) 
		{
			this.#input.setAttribute('accept','image/*');
			this.#input.setAttribute('capture','camera');
		}

		this.#options.container.append(this.#input);		
		this.#input.addEventListener('change',(evt) => this.processUploadEvent(evt) );
		this.#input.click();
	};

	dndUpload()
	{	
		this.#options.dnd.addEventListener('dragover',(evt) => this.dragOver(evt) );
		this.#options.dnd.addEventListener('dragleave',(evt) => this.dragOver(evt) );	
		this.#options.dnd.addEventListener('drop',(evt) => this.processDropEvent(evt) );
	};

	destroy()
	{
		// Abort any active uploads
		this.abort();

		// Clean up drag and drop listeners
		if (this.#options.dnd)
		{
			this.#options.dnd.removeEventListener('dragover');
			this.#options.dnd.removeEventListener('dragleave');
			this.#options.dnd.removeEventListener('drop');
		}

		// Clean up file input
		if (this.#input && this.#input.parentNode) {
			this.#input.parentNode.removeChild(this.#input);
		}
	}

	dragOver(e)
	{
		if (this.#disabled) return;
		
		e.stopPropagation();
		e.preventDefault();
	};

	reset()
	{
		this.#files = [];
		this.#uploaded = 0;
	};

	/**
		* event handlers - collection files and/or directories selected for upload
		*/
	processUploadEvent(evt)
	{
		this.layout();

		this.reset();
		this.#files = Object.values(this.#input.files);
		
		this.#files.forEach( file => this.sendFile(file) );
	};

	processDropEvent(evt)
	{
		if (this.#disabled) return;

		evt.preventDefault();
		evt.stopPropagation();

		this.reset();
		this.layout();


		//passed a folder
		if (evt.dataTransfer.items)
		{
			Object.values( evt.dataTransfer.items ).forEach( rawItem => {
				let item = rawItem.webkitGetAsEntry();
				if (item) this.traverseTree(item,null);
			});
			
		}
		//passed files
		else if (evt.dataTransfer.files)
		{
			Object.values( evt.dataTransfer.files) .forEach( file => {
				this.#files.push(file);
				this.sendFile(file);
			});
		}	

	};

	traverseTree = function (item,path)
	{
		path = path || "";

		if (item.isFile) 
		{
			//send the file for uploading
			item.file( file => {
				this.#files.push(file);
				this.sendFile(file);
			});
		} 
		else if (item.isDirectory) 
		{
			// Get folder contents
			const dirReader = item.createReader();

			//create a new folder on our destination then get the files beneathe it
			this.#fetch.mkcol( item.fullPath ).then( () => {
				dirReader.readEntries( (entries) => {
					entries.forEach( (entry) => this.traverseTree(entry,path + item.name + "/") );
				});
			});

		}

	};

	/**
	 * Upload a single file using the new event-driven WDFetchUpload
	 */
	sendFile(file)
	{
		// Create a new uploader instance for this specific file
		// This allows multiple files to upload simultaneously with individual progress tracking
		// We need to properly clone the original fetch instance to preserve all configuration
		const fileUploader = this.createFileUploader();
		fileUploader.file = file;

		// Store the uploader for this file
		this.#fileUploaders.set(file, fileUploader);

		// Set up event listeners for this specific file upload
		fileUploader.addEventListener('upload:start', (e) => {
			if (!this.#progressWrapper) this.layout();
		});

		fileUploader.addEventListener('upload:progress', (e) => {
			this.handleProgress(e.detail);
		});

		fileUploader.addEventListener('upload:success', (e) => {
			this.#uploaded++;
			this.#fileUploaders.delete(file);
			this.handleComplete(e.detail);

			if (this.#uploaded === this.#files.length) {
				this.handleAllComplete();
			}
		});

		fileUploader.addEventListener('upload:error', (e) => {
			this.#fileUploaders.delete(file);
			this.handleError(e.detail);
		});

		fileUploader.addEventListener('upload:abort', (e) => {
			this.#fileUploaders.delete(file);
			this.handleError({ error: 'Upload was cancelled', ...e.detail });
		});

		// Start the upload
		const uri = (this.#options.putFileName === true) ? '/' + file.name : null;
		
		fileUploader.put(uri).catch(err => {
			// Error handling is already done in event listeners, but this ensures
			// any unhandled promise rejections don't cause issues
			console.error('Upload promise rejected:', err);
		});
	}

	/**
	 * Handle progress updates for all active uploads
	 */
	handleProgress(progressDetail)
	{
		// Calculate total progress across all files
		let totalLoaded = 0;
		let totalSize = 0;

		// Add progress from all files (including completed ones)
		this.#files.forEach(file => {
			const uploader = this.#fileUploaders.get(file);
			if (uploader && uploader.file) {
				// Use the file's current progress
				totalLoaded += uploader.file.loaded || 0;
				totalSize += uploader.file.size || file.size;
			} else {
				// File is completed or not started yet
				totalSize += file.size;
				// If the file is in our uploaded count, it's complete
				if (this.#uploaded > 0) {
					// Estimate based on completed uploads
					const completedFiles = this.#files.slice(0, this.#uploaded);
					if (completedFiles.includes(file)) {
						totalLoaded += file.size;
					}
				}
			}
		});

		// If we have no size data, fall back to simple progress
		if (totalSize === 0) {
			totalSize = this.#files.reduce((sum, file) => sum + file.size, 0);
			totalLoaded = progressDetail.loaded || 0;
		}

		const percentDone = totalSize > 0 ? (totalLoaded / totalSize) * 100 : 0;

		const tlFormatted = this.formatBytes(totalLoaded);
		const tsFormatted = this.formatBytes(totalSize);
		
		this.#progressText.innerText = `Uploaded ${tlFormatted} of ${tsFormatted}`;
		this.#progressStatus.style.width = Math.round(percentDone) + "%";
	}

	/**
	 * Format bytes into human readable format
	 */
	formatBytes(bytes) {
		if (bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
	}
	
	/**
	 * 
	 * @param {*} result - upload result event
	 * called when a single file is finished uploading
	 */
	handleComplete(result)
	{
		this.trigger('upload:complete',result);
	};

	/**
	 * 
	 * @param {*} result 
	 * called when all selected files are finished uploading
	 */
	handleAllComplete()
	{
		this.trigger('uploads:completed');

		this.empty();
		this.remove();
		this.#options.container.style.position = '';
	};

	handleError(err)
	{
		this.trigger('upload:error',err?.response);

		if (err?.response?.message) new WDActivity().showError(err.response.message);

		this.empty();
		this.remove();
		this.#options.container.style.position = '';
	};

	layout()
	{
		this.#progressWrapper = wdc('<div class="wd-uploader-wrapper"/>');

		this.#progressBar = wdc('<div class="wd-uploader-bar"/>');
		this.#progressStatus = wdc('<div class="wd-uploader-status"/>');
		this.#progressBar.append(this.#progressStatus);

		this.#progressText = wdc('<div class="wd-uploader-text"/>');
		this.#progressText.innerText = "Uploading ";

		this.#progressWrapper.append(this.#progressBar);
		this.#progressWrapper.append(this.#progressText);
		this.append(this.#progressWrapper);

		this.#options.container.append(this);
		this.#options.container.style.position = 'relative';
	};

	cleanupCallback()
	{
		// Abort any active uploads
		this.abort();

		// Clean up fetch instance
		if (this.#fetch) {
			if (this.#fetch.abort) this.#fetch.abort();
			this.#fetch = null;
		}

		// Clear file uploaders map
		this.#fileUploaders.clear();

		// Clear references
		this.#input = null;
		this.#files = [];
		this.#progressWrapper = null;
		this.#progressBar = null;
		this.#progressStatus = null;
		this.#progressText = null;
		this.#options = null;

		super.cleanupCallback();
	}

};

customElements.define('wd-uploader',WDUploader);
